/**
 * A marca do arquivo.
 *
 * Nem ícone de navegação nem glifo do léxico — uma terceira coisa, e por isso
 * uma pasta própria. O `Icon.tsx` já explica porque é que aqueles dois vivem
 * separados: um **aponta** para um sítio da app, o outro **codifica** um tipo
 * de evidência e é testado para se distinguir sem cor. Uma marca não faz
 * nenhuma das duas. Se ficasse em `icons/`, mais cedo ou mais tarde alguém a
 * usava como botão; se ficasse em `lexicon/`, entrava nos testes que exigem
 * que cada forma signifique um tipo — e ela não significa nenhum.
 *
 * **Porque é uma letra e não um desenho.** A primeira tentativa foi um eixo
 * com braços a encurtar — a tese do arquivo, o registo que rareia. Vista a
 * 16px numa barra de separadores, era o botão de *alinhar à esquerda* de um
 * processador de texto. Um núcleo estratigráfico saiu pilha; uma lasca de
 * placa saiu ícone de ficheiro. A 16px sobrevivem silhuetas cheias e pouco
 * mais, e uma letra é a silhueta que ninguém confunde com um controlo.
 *
 * **A cor é deliberadamente nenhuma.** Neste produto a cor codifica tipo de
 * entidade e nada mais: nunca um juízo, nunca uma tradição. Pintar a marca de
 * verde-evidência dizia que o arquivo é evidência; de azul-afirmação, que é
 * uma afirmação.
 *
 * O azulejo é `currentColor` e o E é um **furo** — não tinta clara por cima.
 * As fichas não servem aqui: no tema escuro `--et-bar` é #0C0A08 e
 * `--et-cream` #17130F, um azulejo preto com um E quase preto dentro. Sendo
 * furo, o E mostra a página que estiver por trás e a marca inverte-se sozinha
 * nos dois temas, sem uma segunda regra que alguém se esqueça de actualizar.
 */

export interface MarkProps {
  readonly size?: number;
  readonly decorative?: boolean;
  readonly title?: string;
  readonly className?: string;
}

/**
 * O azulejo, e o E como contorno único.
 *
 * O E tem de ser **um** contorno e não um tronco sobreposto a três braços:
 * com `evenodd`, onde duas sub-formas se cruzam a área volta a encher, e os
 * cantos do E apareciam tapados. Traçado assim, de uma vez, não há cruzamento.
 */
const TILE = 'M7 0H25A7 7 0 0 1 32 7V25A7 7 0 0 1 25 32H7A7 7 0 0 1 0 25V7A7 7 0 0 1 7 0Z';
const E = 'M9 6H23V9.7H13V14.15H19.5V17.85H13V22.3H23V26H9Z';

export function Mark({ size = 22, decorative = true, title = 'Evidence Timeline', className }: MarkProps) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 32 32"
      role={decorative ? undefined : 'img'}
      aria-hidden={decorative || undefined}
      aria-label={decorative ? undefined : title}
    >
      {!decorative && <title>{title}</title>}
      <path d={`${TILE}${E}`} fillRule="evenodd" fill="currentColor" />
    </svg>
  );
}
